import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getIconComponent } from '../../utils/toastIconMapping';

interface ErrorLog {
  timestamp: number;
  message: string;
  labels: Record<string, string>;
  severity: string;
  level?: string;
}

interface Toast extends ErrorLog {
  id: string;
  visible: boolean;
  parsedData?: any;
}

interface ToastPopupModalProps {
  showPopup: boolean;
  setShowPopup: (show: boolean) => void;
  toasts: Toast[];
  selectedToastIds: Set<string>;
  handleCheckboxToggle: (toastId: string) => void;
  onClearLogsClick: () => void;
  setSelectedToastIds: (ids: Set<string>) => void;
  displayToast: (message: any, isInPopup?: boolean) => React.ReactNode;
  getSeverityColor: (severity: string, customColor?: string) => { bg: string; border: string; text: string; title: string };
  setSelectedToastForConfirm: (toast: Toast | null) => void;
  setShowConfirmPopup: (show: boolean) => void;
  selectedCustomerName: string;
}

const ToastPopupModal: React.FC<ToastPopupModalProps> = ({
  showPopup,
  setShowPopup,
  toasts,
  selectedToastIds,
  handleCheckboxToggle,
  onClearLogsClick,
  setSelectedToastIds,
  displayToast,
  getSeverityColor,
  setSelectedToastForConfirm,
  setShowConfirmPopup,
  selectedCustomerName
}) => {
  const navigate = useNavigate();

  // Detect icon from message content based on keywords (color is NOT detected, only explicit or severity-based)
  const detectIconFromMessage = (message: string, parsedData?: any) => {
    const fullText = JSON.stringify({ message, ...parsedData }).toLowerCase();

    // Check for keywords in order of priority - only returns icon
    const keywords = [
      { terms: ['asleep', 'sleep', 'sleeping'], icon: 'poweroff' },
      { terms: ['generator', 'genset'], icon: 'generator' },
      { terms: ['power', 'voltage', 'electrical'], icon: 'power' },
      { terms: ['battery', 'ups'], icon: 'battery' },
      { terms: ['network', 'connection', 'connectivity'], icon: 'network' },
      { terms: ['server', 'host'], icon: 'server' },
      { terms: ['database', 'db', 'sql'], icon: 'database' },
      { terms: ['storage', 'disk', 'memory'], icon: 'storage' },
      { terms: ['alarm', 'alert'], icon: 'alarm' },
      { terms: ['warning', 'warn'], icon: 'warning' },
      { terms: ['error', 'fail', 'failed'], icon: 'error' },
      { terms: ['success', 'completed', 'ok'], icon: 'success' },
      { terms: ['running', 'active'], icon: 'running' },
      { terms: ['stopped', 'inactive'], icon: 'stopped' },
      { terms: ['pending', 'waiting'], icon: 'pending' },
      { terms: ['http', 'api', 'endpoint'], icon: 'network' },
    ];

    for (const { terms, icon } of keywords) {
      if (terms.some(term => fullText.includes(term))) {
        return icon;
      }
    }

    return null;
  };

  // Get default icon based on severity level
  const getSeverityDefaultIcon = (severity: string): string => {
    const upperSeverity = severity?.toUpperCase() || '';
    if (upperSeverity === 'CRITICAL' || upperSeverity === 'FATAL') return 'fire';
    if (upperSeverity === 'ERROR') return 'error';
    if (upperSeverity === 'WARN' || upperSeverity === 'WARNING') return 'warning';
    if (upperSeverity === 'ASLEEP') return 'poweroff';
    if (upperSeverity === 'DEBUG') return 'bug';
    if (upperSeverity === 'INFO') return 'info';
    return 'info'; // Default
  };


  if (!showPopup) return null;

  // Helper function to get severity priority (lower number = higher priority)
  const getSeverityPriority = (severity: string): number => {
    const upperSeverity = severity?.toUpperCase() || '';
    if (upperSeverity === 'CRITICAL' || upperSeverity === 'FATAL') return 1;
    if (upperSeverity === 'ERROR') return 2;
    if (upperSeverity === 'WARN' || upperSeverity === 'WARNING') return 3;
    if (upperSeverity === 'ASLEEP') return 4;
    return 5; // DEBUG, INFO, or others
  };

  // Filter and sort toasts by priority
  const visibleToasts = toasts
    .filter(t => t.visible)
    .sort((a, b) => {
      const aPriority = getSeverityPriority(a.severity);
      const bPriority = getSeverityPriority(b.severity);
      return aPriority - bPriority; // Lower priority number comes first
    });

  const totalCount = visibleToasts.length;

  // Count errors and warnings separately
  const errorCount = visibleToasts.filter(t => {
    const severity = t.severity?.toUpperCase();
    return severity === 'ERROR' || severity === 'CRITICAL' || severity === 'FATAL';
  }).length;

  const warningCount = visibleToasts.filter(t => {
    const severity = t.severity?.toUpperCase();
    return severity === 'WARN' || severity === 'WARNING';
  }).length;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: 20
      }}
      onClick={() => {
        setShowPopup(false);
        setSelectedToastIds(new Set());
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          maxWidth: 700,
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* Popup header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              color: '#111827',
              marginBottom: 4
            }}>
              System Alerts
            </h2>
            <p style={{
              margin: 0,
              fontSize: 13,
              color: '#6b7280'
            }}>
              {errorCount > 0 && `${errorCount} error${errorCount !== 1 ? 's' : ''}`}{errorCount > 0 && warningCount > 0 && ', '}{warningCount > 0 && `${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={() => {
              setShowPopup(false);
              setSelectedToastIds(new Set());
            }}
            style={{
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              padding: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              color: '#374151',
              fontSize: 18,
              lineHeight: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
          >
            ✕
          </button>
        </div>

        {/* Popup content - scrollable error list */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: 16
        }}>
          {visibleToasts.map((toast, index, visibleArray) => {
            // PRIORITY 1: Use explicit color/icon from parsedData if provided (case-insensitive keys)
            const customColor = toast.parsedData?.color || toast.parsedData?.Color || toast.parsedData?.COLOR;
            let customIconName = toast.parsedData?.Icon || toast.parsedData?.icon || toast.parsedData?.ICON;

            // PRIORITY 2: Detect icon from message if not explicitly provided (color is NEVER detected)
            if (!customIconName) {
              const detectedIcon = detectIconFromMessage(toast.message, toast.parsedData);
              if (detectedIcon) {
                customIconName = detectedIcon;
              } else {
                // PRIORITY 3: Fall back to severity-based default icon
                customIconName = getSeverityDefaultIcon(toast.severity);
              }
            }

            // Color is always either explicit custom color or severity-based (via getSeverityColor)
            const colors = getSeverityColor(toast.severity, customColor);
            const isChecked = selectedToastIds.has(toast.id);
            const CustomIcon = getIconComponent(customIconName);

            return (
              <div
                key={toast.id}
                style={{
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: index < visibleArray.length - 1 ? 8 : 0,
                  display: 'flex',
                  gap: 10
                }}
              >
                {/* Checkbox on the left */}
                <div style={{ paddingTop: 2 }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckboxToggle(toast.id)}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: 'pointer',
                      accentColor: '#3b82f6'
                    }}
                  />
                </div>

                {/* Toast content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                    gap: 6
                  }}>
                    {/* Left: Badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                      <span style={{
                        background: colors.title,
                        color: 'white',
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: 4,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {toast.severity}
                      </span>
                      {toast.labels?.service && (
                        <span style={{
                          background: '#f3f4f6',
                          color: '#6b7280',
                          fontSize: 10,
                          fontWeight: 500,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid #e5e7eb'
                        }}>
                          {toast.labels.service}
                        </span>
                      )}
                      {toast.labels?.application && (
                        <span style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          fontSize: 10,
                          fontWeight: 500,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid #bfdbfe'
                        }}>
                          APP • {toast.labels.application.toUpperCase()}
                        </span>
                      )}
                      {toast.labels?.namespace && (
                        <span style={{
                          background: '#f3e8ff',
                          color: '#6b21a8',
                          fontSize: 10,
                          fontWeight: 500,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid #e9d5ff'
                        }}>
                          NAMESPACE • {toast.labels.namespace.toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Center: Icon */}
                    {CustomIcon && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minWidth: 40
                      }}>
                        <CustomIcon style={{
                          fontSize: 20,
                          color: colors.title
                        }} />
                      </div>
                    )}

                    {/* Right: Timestamp */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      minWidth: 120
                    }}>
                      <span style={{
                        fontSize: 11,
                        color: '#9ca3af',
                        whiteSpace: 'nowrap'
                      }}>
                        {new Date(toast.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    fontSize: 12,
                    color: colors.text,
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                    marginBottom: 8
                  }}>
                    {displayToast(toast.message, true)}
                  </div>

                  {/* Confirm button for this toast */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedToastForConfirm(toast);
                        setShowConfirmPopup(true);
                      }}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#3b82f6';
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Popup footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Select All checkbox */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              fontSize: 13,
              color: '#374151',
              userSelect: 'none'
            }}>
              <input
                type="checkbox"
                checked={selectedToastIds.size === totalCount && totalCount > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Select all
                    setSelectedToastIds(new Set(visibleToasts.map(t => t.id)));
                  } else {
                    // Deselect all
                    setSelectedToastIds(new Set());
                  }
                }}
                style={{
                  width: 16,
                  height: 16,
                  cursor: 'pointer',
                  accentColor: '#3b82f6'
                }}
              />
              Select All
            </label>

            {/* Clear Logs button - always visible, disabled when no selection */}
            <button
              onClick={() => {
                if (selectedToastIds.size > 0) {
                  onClearLogsClick();
                }
              }}
              disabled={selectedToastIds.size === 0}
              style={{
                background: selectedToastIds.size === 0 ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 500,
                cursor: selectedToastIds.size === 0 ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s ease',
                opacity: selectedToastIds.size === 0 ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (selectedToastIds.size > 0) {
                  e.currentTarget.style.background = '#b91c1c';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedToastIds.size > 0) {
                  e.currentTarget.style.background = '#dc2626';
                }
              }}
            >
              Clear Logs ({selectedToastIds.size})
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => {
                setShowPopup(false);
                setSelectedToastIds(new Set());
                const customerSeg = selectedCustomerName ? `/${encodeURIComponent(selectedCustomerName)}` : '';
                navigate(`${customerSeg}/Logs?timeRange=1m&level=ERROR`);
              }}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#3b82f6';
              }}
            >
              View Full Logs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToastPopupModal;
